import { ReactNode } from "react";

import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";

import Button from "../buttons/Button";
import Tooltip from "./Tooltip";
import FetchErrorAlert from "../errors/FetchErrorAlert";

import styles from "../../styles/ConfirmationDialog.module.less";

const noop = () => undefined;

type ConfirmationDialogProps<T extends FetchError> = {
  title: string;
  description: ReactNode;
  onConfirm: () => void;
  onDeny: () => void;
  confirmText?: string;
  denyText?: string;
  isLoading?: boolean;
  isDenyLoading?: boolean;
  isConfirmDisabled?: boolean;
  confirmDisabledTooltipContent?: string;
  error?: Optional<T>;
  /** A custom render function for the error.
   * If nothing is returned or no method is provided, falls back to default rendering */
  renderError?: (error: Optional<T>) => Optional<ReactNode>;
  /** Any extra content to be displayed after the description, but before the actions */
  children?: ReactNode;
  hasDenyButton?: boolean;
};

const ConfirmationDialog = <T extends FetchError>({
  title,
  description,
  confirmText = "Confirm",
  denyText = "Cancel",
  onConfirm = noop,
  onDeny = noop,
  isLoading,
  isDenyLoading,
  isConfirmDisabled = false,
  confirmDisabledTooltipContent,
  error,
  renderError = noop,
  children,
  hasDenyButton = true,
}: ConfirmationDialogProps<T>) => {
  return (
    <div className={styles.content}>
      {error && (renderError(error) ?? <FetchErrorAlert error={error} />)}
      <div className={styles.text}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.description}>{description}</div>
        <div>{children}</div>
      </div>
      <div className={styles.actions}>
        {hasDenyButton && (
          <Button
            variant="secondary"
            onClick={onDeny}
            isLoading={!isLoading && isDenyLoading}
            disabled={isLoading}
          >
            {denyText}
          </Button>
        )}
        <Tooltip
          isEnabled={!!isConfirmDisabled && !!confirmDisabledTooltipContent}
          content={confirmDisabledTooltipContent}
          position="above"
        >
          <Button
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isConfirmDisabled || isDenyLoading}
          >
            {confirmText}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
